import { z } from 'zod/v4';
import {
  AUDITS_LIST_DEFAULT_PAGE_SIZE,
  AUDITS_LIST_MAX_PAGE_SIZE,
} from './audit-list.js';

/**
 * Query for `GET /api/audits/:id/versions` — legacy param surface verbatim:
 * `page` + `count` only (same bounds/defaults as the list — both came from the
 * legacy `generator.paginationParams`).
 *
 * `strictObject`, unlike the list's stripping schema: the legacy querystring
 * was `additionalProperties:false`, so any other key 400s — fe-v2's
 * `useAuditVersionHistory` deliberately withholds its table's sort/filter
 * state because of this. Preserved so a future param addition is a conscious
 * contract change.
 */
export const AuditVersionsQuerySchema = z.strictObject({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(AUDITS_LIST_MAX_PAGE_SIZE)
    .default(AUDITS_LIST_DEFAULT_PAGE_SIZE),
});

export type AuditVersionsQuery = z.infer<typeof AuditVersionsQuerySchema>;

/**
 * One version-history row on the wire — the legacy versions SELECT (a subset
 * of the list columns: no tierLevel/agents/vendors/requestStatus/digitization,
 * plus the file content-type columns the old FE reads) after the version
 * transformer.
 *
 * `z.looseObject` — undeclared columns ride along (`count` from
 * `COUNT(*) OVER()`). Declared fields are what the frontends read; **this
 * schema is the frontends' type for these rows**.
 *
 * Deliberate deltas vs the legacy wire (audits batch 3, see
 * `docs/migrate-audits-to-modules.md`):
 * - `auditFile`/`capFile` full file objects are DROPPED — no consumer reads
 *   them off version rows (both dashboards read the flat `*FileUrl` /
 *   `*FileContentType` fields), and dropping them kills the legacy per-row
 *   double file re-fetch.
 * - `*FileBucketName` columns are ADDED (the service signs preview URLs from
 *   the already-joined `url`/`bucket_name`, list precedent).
 *
 * Censoring (rows whose access-control `rights` is NULL) masks string values
 * to `'*'`-runs and non-strings to `''`/`null`, so every maskable field stays
 * `z.string().nullable()` — never `z.iso.datetime()`. The `*OriginalUrl`
 * fields are transformer-derived and absent on censored rows.
 * `correctiveActionPlanFileFileType` keeps the legacy double-"File" alias
 * verbatim.
 */
export const AuditVersionRowSchema = z.looseObject({
  // Present on every row — id/companyId/name are censor-exempt; isPublic,
  // rights and privacyStatus are emitted by both branches.
  id: z.string(),
  companyId: z.string(),
  name: z.string().nullable(),
  isPublic: z.boolean(),
  rights: z.array(z.string()).nullable(),
  privacyStatus: z.object({ status: z.enum(['public', 'private']) }),
  companyName: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  auditTypeId: z.string().nullable().optional(),
  auditTypeName: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  auditedBy: z.string().nullable().optional(),
  auditDate: z.string().nullable().optional(),
  referenceNumber: z.string().nullable().optional(),
  facilityScore: z.string().nullable().optional(),
  nextPlannedAuditDate: z.string().nullable().optional(),
  auditReportFileId: z.string().nullable().optional(),
  auditReportFileUrl: z.string().nullable().optional(),
  auditReportFileOriginalUrl: z.string().nullable().optional(),
  auditReportFileBucketName: z.string().nullable().optional(),
  auditReportFileType: z.string().nullable().optional(),
  auditReportFileContentType: z.string().nullable().optional(),
  correctiveActionPlanFileId: z.string().nullable().optional(),
  correctiveActionPlanFileUrl: z.string().nullable().optional(),
  correctiveActionPlanFileOriginalUrl: z.string().nullable().optional(),
  correctiveActionPlanFileBucketName: z.string().nullable().optional(),
  correctiveActionPlanFileFileType: z.string().nullable().optional(),
  correctiveActionPlanFileContentType: z.string().nullable().optional(),
  uploaderCompanyId: z.string().nullable().optional(),
  uploaderCompanyName: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  createdByImpersonateId: z.string().nullable().optional(),
  createdByUserId: z.string().nullable().optional(),
  createdByCompanyId: z.string().nullable().optional(),
  createdByApiKeyId: z.string().nullable().optional(),
  createdByApiKeyLabel: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  updatedByImpersonateId: z.string().nullable().optional(),
  updatedByImpersonateName: z.string().nullable().optional(),
  updatedByUserId: z.string().nullable().optional(),
  updatedByUserName: z.string().nullable().optional(),
  updatedByCompanyId: z.string().nullable().optional(),
  updatedByCompanyName: z.string().nullable().optional(),
  updatedByApiKeyId: z.string().nullable().optional(),
  updatedByApiKeyLabel: z.string().nullable().optional(),
});

export type AuditVersionRow = z.infer<typeof AuditVersionRowSchema>;

export const AuditVersionsResponseSchema = z.object({
  data: z.array(AuditVersionRowSchema),
  meta: z.object({
    count: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
  }),
});

export type AuditVersionsResponse = z.infer<typeof AuditVersionsResponseSchema>;
